import { useEffect, useState } from "react";
import { Button, Card, CardContent, Checkbox, Grid, Paper, Skeleton, Tooltip, Typography } from "@mui/material";
import RemoveIcon from '@mui/icons-material/Remove';

import { useLoadData } from "../../../hooks/useLoadData";
import { planApiKeyStore } from "../../../api/plan-api-key-store";
import { ApiResponse } from "../../../models/api-response";
import { DeleteMapItemComponent } from "./delete-map-item.component";

const styling = {
  labelStyle: { paddingBottom: 5, fontWeight: 'bold', fontSize: 16 },
  moveCardStyle: {
    backgroundColor: '#EBEDEF',
    paddingBottom: 15,
    border: '1px solid #D5D8DC',
    borderRadius: 10,
  },
  itemStyle: {
    padding: '8px',
    margin: '5px 0',
    borderRadius: 5,
    border: '1px solid #D5D8DC',
  },
  newItemStyle: {
    padding: '8px',
    margin: '5px 0',
    borderRadius: 5,
    border: '1px solid #3498DB',
  },
};

interface MappingOfficeCell {
  officeData: any;
  onOfficeRemove: (officeID: number, officeName: string) => void;
}

export const MappingOfficeCell = ({ officeData, onOfficeRemove }: MappingOfficeCell) => {
  const handleOfficeRemove = () => {
    onOfficeRemove(officeData?.officeID, officeData?.officeName);
  };

  return (
    <Paper elevation={0} style={officeData?.isAdded ? styling.newItemStyle : styling.itemStyle}>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography variant="h6" style={{ fontSize: 16 }}>
            {officeData?.officeName}
          </Typography>
        </Grid>
        <Grid item>
          {
            officeData?.officeCheckbox &&
            <Checkbox checked={officeData?.isAdded} onClick={handleOfficeRemove} />
          }
        </Grid>
      </Grid>
    </Paper>
  );
};

interface MappingProjectCell {
  projectData: any;
  officeName: string;
  onProjectRemove: (projectID: number, projectName: string) => void;
}

export const MappingProjectCell = ({ projectData, officeName, onProjectRemove }: MappingProjectCell) => {
  const handleProjectRemove = () => {
    onProjectRemove(projectData?.projectID, projectData?.projectName);
  };
  return (
    <Paper elevation={0} style={projectData?.isAdded ? styling.newItemStyle : styling.itemStyle}>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography variant="h6" style={{ fontSize: 16 }}>
            {projectData?.projectName}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Office: {officeName}
          </Typography>
        </Grid>
        <Grid item>
          {
            projectData?.projectCheckbox &&
            <Checkbox checked={projectData?.isAdded} onClick={handleProjectRemove} />
          }
        </Grid>
      </Grid>
    </Paper>
  );
};

interface MappingUnitCell {
  unitData: any;
  officeName: string;
  projectName: string;
  onUnitRemove: (unitID: number, unitName: string) => void;
}
export const MappingUnitCell = ({ unitData, projectName, officeName, onUnitRemove }: MappingUnitCell) => {
  const handleUnitRemove = () => {
    onUnitRemove(unitData?.unitID, unitData?.unitName);
  };
  return (
    <Paper elevation={0} style={unitData?.isAdded ? styling.newItemStyle : styling.itemStyle}>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography variant="h6" style={{ fontSize: 16 }}>
            {unitData?.unitName}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Office: {officeName}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Project: {projectName}
          </Typography>
        </Grid>
        <Grid item>
          {
            unitData?.unitCheckbox &&
            <Checkbox checked={unitData?.isAdded} onClick={handleUnitRemove} />
          }
        </Grid>
      </Grid>
    </Paper>
  );
};

interface PlanMapListComponent {
  planID: number;
  newMappedData: any[];
  onUpdateMappedData: (data: any) => void;
}

export const PlanMapListComponent = ({ planID, newMappedData, onUpdateMappedData }: PlanMapListComponent) => {

  const [removeData, setRemoveData] = useState<any>({
    id: '',
    name: '',
    type: '',
  });
  const [alertDialog, setAlertDialog] = useState<any>(false);
  const [mappedData, setMappedData] = useState<any>([]);

  const { data, isLoading, isFetching, refetch } = useLoadData<ApiResponse<any>>(
    planApiKeyStore.getMappingPlans(planID),
    !!planID && planID > 0
  );

  useEffect(() => {
    if (data) {
      setMappedData(data?.data?.planOfficeMappings);
      onUpdateMappedData(data?.data?.planOfficeMappings)
    }
  }, [data]);

  useEffect(() => {
    if (newMappedData) {
      setMappedData((prevMappedData: any) => {
        const updatedMappedData = [...(prevMappedData ?? [])];
        let hasChanged = false;

        Array.isArray(newMappedData) &&
          newMappedData?.forEach((newData: any) => {
            const officeIndex = updatedMappedData?.findIndex((item) => item.officeID === newData.officeID);

            if (officeIndex !== -1) {
              const existingData = updatedMappedData[officeIndex];
              Array.isArray(newData.planProjectMappings) &&
                newData.planProjectMappings.forEach((newProject: any) => {
                  const existingProjectIndex = existingData.planProjectMappings.findIndex(
                    (project: any) => project.projectID === newProject.projectID
                  );

                  if (existingProjectIndex !== -1) {
                    const existingProjctData = existingData.planProjectMappings[existingProjectIndex];
                    Array.isArray(newProject.planUnitMappings) &&
                      newProject.planUnitMappings.forEach((newUnit: any) => {
                        const existingUnitIndex = existingProjctData.planUnitMappings.findIndex(
                          (unit: any) => unit.unitID === newUnit.unitID
                        );

                        if (existingUnitIndex === -1) {
                          existingProjctData.planUnitMappings.push(newUnit);
                          hasChanged = true;
                        }
                      });
                  } else {
                    existingData.planProjectMappings.push(newProject);
                    hasChanged = true;
                  }
                });
              updatedMappedData[officeIndex] = existingData;
            } else {
              updatedMappedData.push(newData);
              hasChanged = true;
            }
          });

        return hasChanged ? updatedMappedData : prevMappedData;
      });
    }
  }, [newMappedData]);

  const handleOfficeRemove = (officeID: number, officeName: string) => {
    const updatedData = mappedData?.map((office: any) => {
      if (office.officeID === officeID) {
        return {
          ...office,
          isAdded: !office.isAdded
        };
      }
      return office;
    });
    setMappedData(updatedData);
    onUpdateMappedData(updatedData)
  };

  const handleProjectRemove = (projectID: number, projectName: string) => {
    const updatedData = mappedData?.map((office: any) => {
      return {
        ...office,
        planProjectMappings: office.planProjectMappings.map((project: any) => {
          if (project.projectID === projectID) {
            return {
              ...project,
              isAdded: !project.isAdded
            };
          }
          return project;
        })
      };
    });
    setMappedData(updatedData);
    onUpdateMappedData(updatedData)
  };

  const handleUnitRemove = (unitID: number, unitName: string) => {
    const updatedData = mappedData?.map((office: any) => {
      return {
        ...office,
        planProjectMappings: office.planProjectMappings.map((project: any) => {
          return {
            ...project,
            planUnitMappings: project.planUnitMappings.map((unit: any) => {
              if (unit.unitID === unitID) {
                return {
                  ...unit,
                  isAdded: !unit.isAdded
                };
              }
              return unit;
            })
          };
        })
      };
    });
    setMappedData(updatedData);
    onUpdateMappedData(updatedData)
  };

  const handleClosedDeleteDialog = () => {
    setAlertDialog(false);
    setRemoveData((prevState: any) => ({
      ...prevState,
      id: '',
      name: '',
      type: '',
      projectCount: 0,
      unitCount: 0,
    }));
  };

  return (
    <Card elevation={0} style={{ border: '1px solid #D5D8DC' }}>
      <CardContent>
        {!isLoading || !isFetching ? (
          mappedData?.length > 0 ?
            mappedData?.map((officeMapData: any, index: any) => (
              <Grid container key={`${officeMapData?.officeName}-${officeMapData?.officeID}`}>
                <Grid item xl={12} lg={12} sm={12} xs={12}>
                  <MappingOfficeCell officeData={officeMapData} onOfficeRemove={handleOfficeRemove} />
                  {officeMapData?.planProjectMappings?.map((projectMapData: any) => (
                    <Grid
                      key={`${projectMapData?.projectName}-${projectMapData?.projectID}`}
                      container
                      style={{ paddingLeft: '60px' }}
                    >
                      <Grid item xl={12} lg={12} sm={12} xs={12}>
                        <MappingProjectCell
                          projectData={projectMapData}
                          officeName={officeMapData?.officeName}
                          onProjectRemove={handleProjectRemove}
                        />
                        {projectMapData?.planUnitMappings?.map((unitMapData: any) => (
                          <Grid
                            key={`${unitMapData?.unitName}-${unitMapData?.unitID}`}
                            container
                            style={{ paddingLeft: '100px' }}
                          >
                            <Grid item xl={12} lg={12} sm={12} xs={12}>
                              <MappingUnitCell
                                unitData={unitMapData}
                                projectName={projectMapData?.projectName}
                                officeName={officeMapData?.officeName}
                                onUnitRemove={handleUnitRemove}
                              />
                            </Grid>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            ))
            :
            <div style={{ display: 'flex', justifyContent: 'center', padding: 25 }}>
              <p style={{ textAlign: 'center' }}>
                <b>No data available right now.</b><br />
                Please select a <b>Office</b>, <b>Project</b>, and <b>Unit</b> from the above options and click (<b>+</b>) button to Map.
              </p><br />
            </div>
        ) : (
          <>
            <Skeleton variant="rounded" height={40} style={{ marginBottom: 10 }} />
            <Skeleton variant="rounded" height={40} style={{ marginBottom: 10 }} />
            <Skeleton variant="rounded" height={40} />
          </>
        )}
      </CardContent>
      {/* {alertDialog && (
        <DeleteMapItemComponent removeData={removeData} onRemove={handleRemoveMappedData} onClosed={handleClosedDeleteDialog} />
      )} */}
    </Card>
  );
}